export const TASKS_KEY = ["tasks"] as const;

/**
 * The caller's clients with their counts and logo links. A root of its own: a
 * draft's create or delete changes a card's counts, and marks this stale.
 */
export const CLIENTS_KEY = ["clients"] as const;

/** What each Intake slot takes: one table for every draft, outside any task's key. */
export const INTAKE_SLOTS_KEY = ["intake-slots"] as const;

/** What the signed-in user still has to accept before the app is usable. */
export const CONSENT_KEY = ["consent"] as const;

/** The signed-in attorney's own defaults for their claims, which each new draft starts with. */
export const CLAIM_PREFERENCES_KEY = ["preferences", "claims"] as const;

export function taskDetailKey(taskId: string) {
  return [...TASKS_KEY, taskId] as const;
}

/** Its own key rather than a slice of the task: the layout is written whole. */
export function figuresKey(taskId: string) {
  return [...TASKS_KEY, taskId, "figures"] as const;
}

/** Its own key: the Detailed Description framework is served whole and replaced as a unit. */
export function ddFrameworkKey(taskId: string) {
  return [...TASKS_KEY, taskId, "dd-framework"] as const;
}

/** Its own key like the figures': the set is served whole and replaced as a unit. */
export function inventionElementsKey(taskId: string) {
  return [...TASKS_KEY, taskId, "invention", "elements"] as const;
}

/** The terms of the elements and rungs: under the elements' key, since a reworded element moves its term's place. */
export function inventionNamesKey(taskId: string) {
  return [...inventionElementsKey(taskId), "names"] as const;
}

/** What one element cites in one file: under the elements' key, so a write to the set drops it too. */
export function passagesKey(
  taskId: string,
  fileId: string,
  elementId: string | null,
) {
  return [
    ...inventionElementsKey(taskId),
    "passages",
    fileId,
    elementId,
  ] as const;
}

/**
 * A file's short-lived viewing URL. Its own root rather than the task's: the
 * URL is not part of the task's served state, so no invalidation of the task
 * by prefix should reach it while a drawer is open on the file.
 */
export function fileUrlKey(taskId: string, fileId: string) {
  return ["file-url", taskId, fileId] as const;
}

/** A task's claim strategy stage: the set, its projections, the proposal run and the gate, served whole. */
export function claimStrategyKey(taskId: string) {
  return [...TASKS_KEY, taskId, "claim-strategy"] as const;
}

/** A task's positions: one aggregate, replaced whole on every write. */
export function positionsKey(taskId: string) {
  return [...TASKS_KEY, taskId, "positions"] as const;
}

/** Every served gate of one task, whatever inputs it was keyed by: what the stream marks stale. */
export function noveltyGateScope(taskId: string) {
  return [...TASKS_KEY, taskId, "novelty-gate"] as const;
}

/** Whether Claim Strategy, Specification and Generate are open, with each criterion's count. */
export function stageGateKey(taskId: string) {
  return [...TASKS_KEY, taskId, "stage-gate"] as const;
}

/**
 * Every served read whose gate reads the thesis: the novelty gate, whose commit
 * reads whether each position is settled, the claim strategy with its lock, and
 * the stage gate, whose criteria count the confirmed elements, positions and
 * assessments. A write that can move the thesis can unsettle a position, so it
 * asks them all again, and no rail mark stands on an answer the server would no
 * longer give.
 */
export function thesisGateScopes(taskId: string) {
  return [
    noveltyGateScope(taskId),
    claimStrategyKey(taskId),
    stageGateKey(taskId),
  ] as const;
}

/** The served run gate, keyed by what the screen knows the gate reads, so a moved input refetches it. */
export function noveltyGateKey(taskId: string, fingerprint: string) {
  return [...noveltyGateScope(taskId), fingerprint] as const;
}

/** The finished draft's survey: one per draft, since each draft is surveyed on its own. */
export function draftSurveyKey(taskId: string) {
  return [...TASKS_KEY, taskId, "generate", "survey"] as const;
}

/**
 * The review before a draft is generated. Under the task's key, so a write to
 * any record it shows (a rename among them) reads it again.
 */
export function generateReviewKey(taskId: string) {
  return [...TASKS_KEY, taskId, "generate", "review"] as const;
}

/** The task's draft run queued last, which the Generate pages read. */
export function latestRunKey(taskId: string) {
  return [...TASKS_KEY, taskId, "generate", "latest-run"] as const;
}

/** The served analysis: one read for the whole tab, invalidated by every position write and by a settled run. */
export function noveltyAnalysisKey(taskId: string) {
  return [...TASKS_KEY, taskId, "novelty-analysis"] as const;
}

/** Under the task's key, so whatever reads the task again reads its stages' sources again too. */
export function stageSourcesKey(taskId: string, stage: string) {
  return [...stageSourcesScope(taskId), stage] as const;
}

/** Every stage's sources for a draft, for a write that moves what more than one stage reads. */
export function stageSourcesScope(taskId: string) {
  return [...TASKS_KEY, taskId, "sources"] as const;
}

/** The task's classification: the suggested codes, the attorney's choice and its run. */
export function classificationKey(taskId: string) {
  return [...TASKS_KEY, taskId, "classification"] as const;
}

/** A search of the CPC scheme, which every task reads alike. */
export function cpcSearchKey(query: string) {
  return ["cpc-search", query] as const;
}

/** The changes an impact assessment suggested, read again whenever a record moves on the stream. */
export function notificationsKey(taskId: string) {
  return [...TASKS_KEY, taskId, "notifications"] as const;
}

/** The questions Praxis asked on the task, read again whenever a queue moves on the stream. */
export function questionsKey(taskId: string) {
  return [...TASKS_KEY, taskId, "questions"] as const;
}

/** Every term the draft will use: read again after any edit, since any record may name one. */
export function termsKey(taskId: string) {
  return [...TASKS_KEY, taskId, "terms"] as const;
}
